// categoriesAsset requires use of <Suspense>
import { isLocalhost } from 'packages/main/serviceWorkerRegistration';

import { Snapshot } from 'recoil';
import {
  curAnnotationTypesAtom,
  waveSurferObjFamily,
} from 'packages/main/components/audio-tool/atoms';
import {
  AnnotationRegion,
  AudioAnnotationTypes,
  getRegionColorForType,
  RegionColors,
} from 'packages/main/components/audio-tool/audio-tool-types';
import type { Region } from 'wavesurfer.js/src/plugin/regions';
import { compact, sumBy } from 'lodash';
import type { Category } from 'packages/lib/api/annotations/contract/category';
import type { WaveSurferBackend } from 'wavesurfer.js/types/backend';

export async function getActiveWaveSurfers(
  snapshot: Snapshot,
  annotationTypes: AudioAnnotationTypes[]
): Promise<(WaveSurfer | null)[]> {
  return Promise.all<WaveSurfer | null>(
    annotationTypes.map((annotationType) =>
      snapshot.getPromise(waveSurferObjFamily(annotationType))
    )
  );
}

export const timeToMinSecString = (timeInSec: number | undefined): string => {
  if (!timeInSec) return '0:00';
  const result = new Date(timeInSec * 1_000).toISOString().substring(11);
  // strip hours if '00:'
  let resultB = result.startsWith('00:')
    ? result.substring(3)
    : result.substring(0);
  // Strip leading 0
  resultB = resultB.startsWith('0') ? resultB.substring(1) : resultB;
  return resultB.replace('Z', '');
};

export function updateRegionColor(
  region: Region,
  regionIsSelected: boolean,
  regionIsBeingPlayed: boolean,
  regionIsHighlighted: boolean
): void {
  const { annotationType } = region as AnnotationRegion;
  const unselectedColor = annotationType
    ? getRegionColorForType(annotationType, 0.7)
    : RegionColors.Unselected;
  const highlightedColor = annotationType
    ? getRegionColorForType(annotationType, 0.4)
    : RegionColors.Unselected;

  region.update({
    ...region,
    color: regionIsSelected
      ? RegionColors.Selected
      : //   : regionIsBeingPlayed
      //   ? RegionColors.Playing
      regionIsHighlighted
      ? highlightedColor
      : unselectedColor,
  });
}

export function getSirenCategoryTokenFromName(
  sirenCategoryName: string,
  categories: Category[]
): string {
  return categories.find((c) => c.name === sirenCategoryName)?.token ?? '';
}

export function getSirenCategoryNameFromToken(
  token: string,
  categories: Category[]
): string {
  return categories.find((c) => c.token === token)?.name ?? '';
}

type ConsoleLogArgs = [message: string, ...optionalParams: unknown[]];

// Useful for debug messages while developing, but they only show up on localhost
export function devLog(...params: ConsoleLogArgs): void {
  if (isLocalhost) {
    console.log(...params);
  }
}

// For use within a useRecoilCallback
export async function countNumRegionsTotal(
  snapshot: Snapshot,
  categories?: string[]
): Promise<number> {
  const annotationCategories =
    categories || (await snapshot.getPromise(curAnnotationTypesAtom));

  const atoms = annotationCategories.map((cat) =>
    waveSurferObjFamily(cat as AudioAnnotationTypes)
  );
  const wavesurferObjs = await Promise.all(atoms.map(snapshot.getPromise));

  return sumBy(
    compact(wavesurferObjs),
    (ws) => Object.keys(ws.regions.list).length
  );
}

// Wavesurfer does not have the ability to mute specific channels built-in. So we used a fixed variation of the hack here:
// https://stackoverflow.com/questions/52985129/unable-to-mute-one-channel-in-wavesurfer-js
export function muteChannels(
  waveSurferObj: WaveSurfer,
  savedBufferData: Float32Array[],
  mutedData: Float32Array,
  curChannels: Set<number>
): Promise<void> {
  return new Promise((resolve: (value: void) => void) => {
    const { buffer } = waveSurferObj.backend as WaveSurferBackend & {
      buffer: AudioBuffer;
    };
    if (buffer) {
      for (let n = 0; n < buffer.numberOfChannels; n++) {
        buffer.copyToChannel(
          curChannels.has(n) ? savedBufferData[n] : mutedData,
          n
        );
      }

      waveSurferObj.drawBuffer();
      resolve();
    }
  });
}
